import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';

import { API_BASE_URL } from '../../lib/api';

type UploadKind = 'image' | 'document' | 'vcard';

type UploadRecord = {
  id: string;
  name: string;
  kind: UploadKind;
  mimeType: string | null;
  url: string;
  uploadedAt: string;
  sizeBytes: number | null;
};

type UploadApiPayload = {
  id: string;
  fileName?: string;
  url?: string;
  fileUrl?: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  kind?: string | null;
  uploadedAt?: string;
};

type UploadIntentPayload = {
  id: string;
  uploadUrl: string;
  expiresAt: string;
  headers: Record<string, string>;
};

type UploadDrawerProps = {
  open: boolean;
  onClose: () => void;
  onInsert: (record: UploadRecord) => void;
  apiBaseUrl?: string;
  slug?: string;
};

const normalizeKind = (value: string | null | undefined): UploadKind => {
  if (value === 'image') {
    return 'image';
  }
  if (value === 'vcard') {
    return 'vcard';
  }
  return 'document';
};

const formatFileSize = (bytes: number | null): string => {
  if (!bytes || Number.isNaN(bytes) || bytes <= 0) {
    return '';
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ['KB', 'MB', 'GB'] as const;
  let size = bytes / 1024;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const mapUploadPayload = (payload: UploadApiPayload): UploadRecord => ({
  id: String(payload.id),
  name: payload.fileName ?? 'upload',
  kind: normalizeKind(payload.kind),
  mimeType: payload.mimeType ?? null,
  url: payload.url ?? payload.fileUrl ?? '',
  uploadedAt: payload.uploadedAt ?? new Date().toISOString(),
  sizeBytes: typeof payload.sizeBytes === 'number' ? payload.sizeBytes : null
});

const readFileArrayBuffer = (file: File) => {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read file'));
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.readAsArrayBuffer(file);
  });
};

const sha256Base64 = async (file: File) => {
  const digest = await crypto.subtle.digest('SHA-256', await readFileArrayBuffer(file));
  const bytes = new Uint8Array(digest);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const UploadDrawer = ({ open, onClose, onInsert, apiBaseUrl, slug }: UploadDrawerProps) => {
  const [uploads, setUploads] = useState<UploadRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; record: UploadRecord } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const endpoint = useMemo(() => (apiBaseUrl ?? API_BASE_URL).replace(/\/$/, ''), [apiBaseUrl]);
  const isConnected = Boolean(endpoint && slug);

  const groupedUploads = useMemo(() => ({
    vcard: uploads.filter((record) => record.kind === 'vcard'),
    image: uploads.filter((record) => record.kind === 'image'),
    document: uploads.filter((record) => record.kind === 'document')
  }), [uploads]);

  const fetchUploads = useCallback(async () => {
    if (!isConnected || !slug) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${endpoint}/uploads/${encodeURIComponent(slug)}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null) as { message?: string } | null;
        throw new Error(payload?.message ?? 'Failed to load uploads');
      }

      const payload = await response.json() as { uploads?: UploadApiPayload[] };
      setUploads((payload.uploads ?? []).map(mapUploadPayload));
    } catch (loadError) {
      console.warn('Failed to fetch uploads', loadError);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load uploads');
      setUploads([]);
    } finally {
      setIsLoading(false);
    }
  }, [endpoint, isConnected, slug]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!isConnected) {
      setError('Connect to InkTap to store files in the cloud');
      setUploads([]);
      return;
    }

    void fetchUploads();
  }, [open, isConnected, fetchUploads]);

  useEffect(() => {
    if (!open) {
      setContextMenu(null);
    }
  }, [open]);

  useEffect(() => {
    if (!contextMenu) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && menuRef.current.contains(event.target as Node)) {
        return;
      }
      setContextMenu(null);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setContextMenu(null);
      }
    };

    window.addEventListener('click', handleOutsideClick);
    window.addEventListener('contextmenu', handleOutsideClick);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('click', handleOutsideClick);
      window.removeEventListener('contextmenu', handleOutsideClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu]);

  useEffect(() => {
    if (!isConnected) {
      setUploads([]);
    }
  }, [isConnected]);

  const handleUpload = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    if (!isConnected || !slug) {
      setError('You need to be signed in to upload files.');
      event.target.value = '';
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const checksumSha256 = await sha256Base64(file);
      const intentResponse = await fetch(`${endpoint}/uploads/${encodeURIComponent(slug)}/intents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          checksumSha256
        }),
        credentials: 'include'
      });
      if (!intentResponse.ok) {
        const payload = await intentResponse.json().catch(() => null) as { message?: string } | null;
        throw new Error(payload?.message ?? 'Upload failed');
      }
      const { intent } = await intentResponse.json() as { intent: UploadIntentPayload };
      const objectResponse = await fetch(intent.uploadUrl, {
        method: 'PUT',
        headers: intent.headers,
        body: file
      });
      if (!objectResponse.ok) {
        throw new Error('Object storage rejected the upload. Please try again.');
      }
      const finalizeResponse = await fetch(
        `${endpoint}/uploads/${encodeURIComponent(slug)}/intents/${encodeURIComponent(intent.id)}/finalize`,
        { method: 'POST', credentials: 'include' }
      );
      if (!finalizeResponse.ok) {
        const finalizePayload = await finalizeResponse.json().catch(() => null) as { message?: string } | null;
        throw new Error(finalizePayload?.message ?? 'Upload validation failed');
      }
      const payload = await finalizeResponse.json() as { upload: UploadApiPayload };
      const record = mapUploadPayload(payload.upload);
      setUploads((current) => [record, ...current]);
    } catch (uploadError) {
      console.warn('Upload failed', uploadError);
      setError(uploadError instanceof Error ? uploadError.message : 'Upload failed');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  }, [endpoint, isConnected, slug]);

  const handleRemove = useCallback(async (id: string) => {
    if (!isConnected || !slug) {
      setError('Connect to InkTap before removing files.');
      return;
    }

    try {
      const response = await fetch(`${endpoint}/uploads/${encodeURIComponent(slug)}/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok && response.status !== 204) {
        const payload = await response.json().catch(() => null) as { message?: string } | null;
        throw new Error(payload?.message ?? 'Failed to delete upload');
      }

      setUploads((current) => current.filter((record) => record.id !== id));
    } catch (deleteError) {
      console.warn('Failed to remove upload', deleteError);
      setError(deleteError instanceof Error ? deleteError.message : 'Failed to delete upload');
    }
  }, [endpoint, isConnected, slug]);

  const handleImageContextMenu = useCallback(
    (event: ReactMouseEvent<HTMLElement>, record: UploadRecord) => {
      event.preventDefault();
      event.stopPropagation();

      const menuWidth = 200;
      const menuHeight = 96;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let x = event.clientX;
      let y = event.clientY;

      if (x + menuWidth > viewportWidth) {
        x = viewportWidth - menuWidth - 12;
      }
      if (y + menuHeight > viewportHeight) {
        y = viewportHeight - menuHeight - 12;
      }

      setContextMenu({
        x: Math.max(x, 12),
        y: Math.max(y, 12),
        record
      });
    },
    []
  );

  const handleContextInsert = useCallback(
    (record: UploadRecord) => {
      onInsert(record);
      setContextMenu(null);
    },
    [onInsert]
  );

  const handleContextRemove = useCallback(
    (record: UploadRecord) => {
      void handleRemove(record.id);
      setContextMenu(null);
    },
    [handleRemove]
  );

  const handleCopyLink = useCallback(async (record: UploadRecord) => {
    try {
      await navigator.clipboard.writeText(record.url);
      setError(null);
    } catch (copyError) {
      console.warn('Clipboard write failed', copyError);
      setError('Unable to copy link to clipboard in this browser.');
    }
  }, []);

  if (!open) {
    return null;
  }

  return (
    <div className='upload-drawer' role='dialog' aria-modal='true' aria-labelledby='upload-drawer-title'>
      <div className='upload-drawer__panel'>
        <header className='upload-drawer__header'>
          <div>
            <h2 id='upload-drawer-title'>Uploads</h2>
            <p className='helper-text'>
              Store images, PDFs, and vCards in InkTap. Insert them into your page in one click.
            </p>
          </div>
          <button type='button' onClick={onClose} aria-label='Close upload drawer'>
            Close
          </button>
        </header>

        <label className='upload-drawer__picker'>
          <input
            type='file'
            accept='image/png,image/jpeg,image/webp,image/gif,application/pdf,.pdf,text/vcard,.vcf'
            onChange={handleUpload}
            disabled={isUploading || !isConnected}
          />
          <span>
            {isConnected ? (isUploading ? 'Uploading...' : 'Upload a file') : 'Connect to InkTap to upload'}
          </span>
        </label>

        {error ? <p className='upload-drawer__error'>{error}</p> : null}
        {isLoading ? <p className='helper-text'>Loading your uploads...</p> : null}

        {!isConnected ? (
          <p className='helper-text'>
            Make sure you're signed in and have a landing page link assigned before uploading files.
          </p>
        ) : null}

        <section className='upload-drawer__section'>
          <h3>vCards</h3>
          {groupedUploads.vcard.length === 0 ? (
            <p className='helper-text'>Add a .vcf file so visitors can save your contact details.</p>
          ) : (
            <ul className='upload-drawer__list'>
              {groupedUploads.vcard.map((record) => (
                <li key={record.id}>
                  <div>
                    <strong>{record.name}</strong>
                    <span>Added {new Date(record.uploadedAt).toLocaleString()}</span>
                  </div>
                  <div className='upload-drawer__actions'>
                    <button type='button' onClick={() => onInsert(record)}>Insert into page</button>
                    <button type='button' onClick={() => handleCopyLink(record)}>Copy link</button>
                    <button type='button' onClick={() => handleRemove(record.id)}>Remove</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className='upload-drawer__section'>
          <h3>Images</h3>
          {groupedUploads.image.length === 0 ? (
            <p className='helper-text'>Upload a profile photo or background image.</p>
          ) : (
            <ul className='upload-drawer__list upload-drawer__list--media'>
              {groupedUploads.image.map((record) => (
                <li key={record.id} onContextMenu={(event) => handleImageContextMenu(event, record)}>
                  <img src={record.url} alt={record.name} onContextMenu={(event) => handleImageContextMenu(event, record)} />
                  <div className='upload-drawer__list-text'>
                    <strong>{record.name}</strong>
                    <span>{record.mimeType?.toUpperCase() ?? 'IMAGE'}</span>
                    <span>{formatFileSize(record.sizeBytes)}</span>
                  </div>
                  <div className='upload-drawer__actions'>
                    <button type='button' onClick={() => onInsert(record)}>Insert into page</button>
                    <button type='button' onClick={() => handleRemove(record.id)}>Remove</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className='upload-drawer__section'>
          <h3>Documents</h3>
          {groupedUploads.document.length === 0 ? (
            <p className='helper-text'>Share brochures or menus as PDF downloads.</p>
          ) : (
            <ul className='upload-drawer__list'>
              {groupedUploads.document.map((record) => (
                <li key={record.id}>
                  <div>
                    <strong>{record.name}</strong>
                    <span>{record.mimeType?.toUpperCase() ?? 'FILE'}{formatFileSize(record.sizeBytes) ? ` - ${formatFileSize(record.sizeBytes)}` : ''}</span>
                  </div>
                  <div className='upload-drawer__actions'>
                    <button type='button' onClick={() => onInsert(record)}>Insert into page</button>
                    <button type='button' onClick={() => handleCopyLink(record)}>Copy link</button>
                    <button type='button' onClick={() => handleRemove(record.id)}>Remove</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      {contextMenu ? (
        <div
          ref={menuRef}
          className='upload-drawer__context-menu'
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
          role='menu'
        >
          <button type='button' onClick={() => handleContextInsert(contextMenu.record)}>Add to card</button>
          <button type='button' onClick={() => handleContextRemove(contextMenu.record)}>Delete</button>
        </div>
      ) : null}
    </div>
  );
};

export type { UploadRecord };
export { UploadDrawer };
